/**
 * useVideoFade - Custom requestAnimationFrame fade loop for the hero video.
 *
 * Why not CSS transitions?
 *   We need the fade to start exactly when the video begins canplay'ing,
 *   and to fade out 0.55s before the clip ends. CSS transitions can't
 *   express "fade out near the natural end time" cleanly, and they
 *   snap on opacity reset between loops.
 *
 *   This RAF approach:
 *     - starts hidden (opacity 0)
 *     - fades in 500ms on canplay
 *     - fades out 500ms when 0.55s remain  (fadingOutRef stops repeats)
 *     - on ended: sets opacity 0, waits 100ms, resets to currentTime=0,
 *       plays, fades back in 500ms
 *     - cancels any in-flight RAF before starting a new fade so we don't
 *       have two animations racing.
 *
 * Attach the returned ref to your <video> element. Set initial style
 * opacity:0 on the element to avoid a frame of full opacity before
 * canplay fires.
 */
import { useEffect, useRef } from "react";

export function useVideoFade() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const fadingOutRef = useRef(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const cancelRaf = () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };

    const fadeTo = (target: number, duration = 500) => {
      cancelRaf();
      const start = performance.now();
      const from = parseFloat(v.style.opacity || (target === 1 ? "0" : "1"));
      const step = (t: number) => {
        const p = Math.min(1, (t - start) / duration);
        const cur = from + (target - from) * p;
        v.style.opacity = String(cur);
        if (p < 1) rafRef.current = requestAnimationFrame(step);
        else rafRef.current = null;
      };
      rafRef.current = requestAnimationFrame(step);
    };

    const onCanPlay = () => {
      v.muted = true;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
      fadingOutRef.current = false;
      fadeTo(1, 500);
    };

    const onTimeUpdate = () => {
      if (!isFinite(v.duration)) return;
      const remaining = v.duration - v.currentTime;
      if (remaining <= 0.55 && !fadingOutRef.current) {
        fadingOutRef.current = true;
        fadeTo(0, 500);
      }
    };

    const onEnded = () => {
      v.style.opacity = "0";
      setTimeout(() => {
        v.currentTime = 0;
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
        fadingOutRef.current = false;
        fadeTo(1, 500);
      }, 100);
    };

    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("ended", onEnded);
    return () => {
      cancelRaf();
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("ended", onEnded);
    };
  }, []);

  return videoRef;
}
